import { css } from '@emotion/react';

import { KAI_SHIKI_LAYOUT, KAI_SHIKI_TYPOGRAPHY, styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily, styleguideSpacing } from '@kaiizen/kai-shiki/core';

export const TURN_BASED_DASHBOARD_SHELL_STYLES = {
  page: css`
    label: TURN_BASED_DASHBOARD_PAGE;
    display: flex;
    min-height: 100%;
    background: ${styleguideColors.sunken};
  `,
  sidebar: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR;
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    align-self: flex-start;
    width: calc(${styleguideSpacing.s8}px * 3 + ${styleguideSpacing.s5}px + ${styleguideSpacing.s4}px);
    min-width: calc(${styleguideSpacing.s8}px * 3 + ${styleguideSpacing.s5}px + ${styleguideSpacing.s4}px);
    height: calc(100vh - ${KAI_SHIKI_LAYOUT.topBarHeight}px);
    box-sizing: border-box;
    padding: ${styleguideSpacing.s4}px 0;
    background: ${styleguideColors.paper};
    border-right: 1px solid ${styleguideColors.stroke};
    overflow: hidden;
  `,
  sidebarTitle: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_TITLE;
    margin: 0 calc(${styleguideSpacing.s4}px + ${styleguideSpacing.s1}px) ${styleguideSpacing.s2}px;
    color: ${styleguideColors.textTertiary};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    text-transform: uppercase;
    flex-shrink: 0;
  `,
  sidebarTitleAfterWork: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_TITLE_AFTER_WORK;
    margin-top: ${styleguideSpacing.s5}px;
  `,
  sidebarWorkList: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_WORK_LIST;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  `,
  sidebarMarketList: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_MARKET_LIST;
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  `,
  sidebarItem: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_ITEM;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${styleguideSpacing.s2}px;
    min-height: ${styleguideSpacing.s6}px;
    padding: 0 calc(${styleguideSpacing.s4}px + ${styleguideSpacing.s1}px);
    border: 0;
    background: transparent;
    color: ${styleguideColors.ink};
    cursor: pointer;
    text-align: left;
    ${KAI_SHIKI_TYPOGRAPHY.small}

    &:hover {
      background: ${styleguideColors.wash};
    }
  `,
  sidebarItemSelected: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_ITEM_SELECTED;
    background: ${styleguideColors.selectedWash};
    box-shadow: inset 3px 0 0 0 ${styleguideColors.accentDeep};
    font-weight: ${styleguideFontWeight.bold};

    &:hover {
      background: ${styleguideColors.selectedWash};
    }
  `,
  sidebarItemName: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_ITEM_NAME;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  sidebarItemCount: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_ITEM_COUNT;
    flex-shrink: 0;
    color: ${styleguideColors.textSecondary};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    font-variant-numeric: tabular-nums;
  `,
  sidebarFooter: css`
    label: TURN_BASED_DASHBOARD_SIDEBAR_FOOTER;
    flex-shrink: 0;
    padding: ${styleguideSpacing.s3}px calc(${styleguideSpacing.s4}px + ${styleguideSpacing.s1}px);
    border-top: 1px solid ${styleguideColors.stroke};
  `,
  addMarketButton: css`
    label: TURN_BASED_DASHBOARD_ADD_MARKET_BUTTON;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${styleguideSpacing.s1}px;
    width: 100%;
    min-height: ${styleguideSpacing.s5}px;
    border: 1px solid ${styleguideColors.ink};
    border-radius: 0;
    background: transparent;
    color: ${styleguideColors.ink};
    cursor: pointer;
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};

    &:hover {
      background: ${styleguideColors.wash};
    }
  `,
  main: css`
    label: TURN_BASED_DASHBOARD_MAIN;
    flex: 1;
    min-width: 0;
    padding: ${styleguideSpacing.s4}px ${styleguideSpacing.s5}px ${styleguideSpacing.s6}px;
  `,
  mainContent: css`
    label: TURN_BASED_DASHBOARD_MAIN_CONTENT;
    width: 100%;
    max-width: calc(${styleguideSpacing.s8}px * 20 + ${styleguideSpacing.s6}px + ${styleguideSpacing.s2}px);
    margin: 0 auto;
  `,
  projectList: css`
    label: TURN_BASED_DASHBOARD_PROJECT_LIST;
    display: flex;
    flex-direction: column;
    gap: ${styleguideSpacing.s2}px;
    padding-top: ${styleguideSpacing.s3}px;
  `,
  emptyState: css`
    label: TURN_BASED_DASHBOARD_EMPTY_STATE;
    padding: calc(${styleguideSpacing.s8}px * 2) ${styleguideSpacing.s3}px;
    color: ${styleguideColors.textSecondary};
    text-align: center;
    ${KAI_SHIKI_TYPOGRAPHY.medium}
  `,
  loadMore: css`
    label: TURN_BASED_DASHBOARD_LOAD_MORE;
    display: block;
    min-height: ${styleguideSpacing.s5}px;
    margin: ${styleguideSpacing.s3}px auto 0;
    padding: 0 ${styleguideSpacing.s3}px;
    border: 1px solid ${styleguideColors.ink};
    border-radius: 0;
    background: ${styleguideColors.paper};
    color: ${styleguideColors.ink};
    cursor: pointer;
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
  `,
};
